import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, RotateCcw, Download } from 'lucide-react';

interface ScreenshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the cropped PNG blob when user confirms setting as thumbnail */
  onSetThumbnail?: (blob: Blob) => void;
  /** Whether the model has been saved (required for thumbnail upload) */
  canSetThumbnail?: boolean;
}

interface CropBox {
  x: number;
  y: number;
  size: number;
}

/**
 * Find the Three.js / R3F canvas in the DOM.
 * R3F sets a `data-engine` attribute on its canvas element.
 * Falls back to the largest canvas if attribute isn't found.
 */
export function findR3FCanvas(): HTMLCanvasElement | null {
  const canvases = document.querySelectorAll('canvas');

  for (const canvas of canvases) {
    if (canvas.getAttribute('data-engine')) {
      return canvas;
    }
  }

  let largest: HTMLCanvasElement | null = null;
  let largestArea = 0;
  for (const canvas of canvases) {
    const area = canvas.width * canvas.height;
    if (area > largestArea) {
      largestArea = area;
      largest = canvas;
    }
  }
  return largest;
}

export function captureR3FCanvas(): string | null {
  const canvas = findR3FCanvas();
  if (!canvas) return null;
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

type Step = 'crop' | 'confirm';

export function ScreenshotDialog({
  open,
  onOpenChange,
  onSetThumbnail,
  canSetThumbnail = false,
}: ScreenshotDialogProps) {
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [displayDimensions, setDisplayDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [cropBox, setCropBox] = useState<CropBox>({ x: 0, y: 0, size: 100 });
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState<Step>('crop');
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(
    null,
  );
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);

  const dragStateRef = useRef<{
    type: 'none' | 'drag' | 'resize';
    startMouseX: number;
    startMouseY: number;
    startCropX: number;
    startCropY: number;
    startCropSize: number;
  }>({
    type: 'none',
    startMouseX: 0,
    startMouseY: 0,
    startCropX: 0,
    startCropY: 0,
    startCropSize: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const displayDimsRef = useRef(displayDimensions);
  displayDimsRef.current = displayDimensions;
  const cropBoxRef = useRef(cropBox);
  cropBoxRef.current = cropBox;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        const dataUrl = captureR3FCanvas();
        setImageDataUrl(dataUrl);
        setStep('crop');
        setCroppedPreviewUrl(null);
        setCroppedBlob(null);
      } else {
        setImageDataUrl(null);
        setDisplayDimensions({ width: 0, height: 0 });
        dragStateRef.current.type = 'none';
        setCroppedPreviewUrl(null);
        setCroppedBlob(null);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleImageLoad = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const naturalW = imageRef.current.naturalWidth;
    const naturalH = imageRef.current.naturalHeight;
    if (naturalW === 0 || naturalH === 0) return;

    const imgAspect = naturalW / naturalH;
    const containerAspect = containerWidth / containerHeight;

    let displayWidth: number;
    let displayHeight: number;

    if (imgAspect > containerAspect) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / imgAspect;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * imgAspect;
    }

    setDisplayDimensions({ width: displayWidth, height: displayHeight });

    const initialSize = Math.min(displayWidth, displayHeight) * 0.8;
    setCropBox({
      x: (displayWidth - initialSize) / 2,
      y: (displayHeight - initialSize) / 2,
      size: initialSize,
    });
  }, []);

  const clampCropBox = useCallback((box: CropBox): CropBox => {
    const d = displayDimsRef.current;
    const minSize = 50;
    const size = Math.max(minSize, Math.min(box.size, d.width, d.height));
    const x = Math.max(0, Math.min(box.x, d.width - size));
    const y = Math.max(0, Math.min(box.y, d.height - size));
    return { x, y, size };
  }, []);

  const getImageOffset = useCallback(() => {
    if (!containerRef.current) return { left: 0, top: 0 };
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const d = displayDimsRef.current;
    return {
      left: (containerWidth - d.width) / 2,
      top: (containerHeight - d.height) / 2,
    };
  }, []);

  const getImageCoords = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      const offset = getImageOffset();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: e.clientX - rect.left - offset.left,
        y: e.clientY - rect.top - offset.top,
      };
    },
    [getImageOffset],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const state = dragStateRef.current;
      if (state.type === 'none') return;
      e.preventDefault();

      const coords = getImageCoords(e);

      if (state.type === 'drag') {
        const dx = coords.x - state.startMouseX;
        const dy = coords.y - state.startMouseY;
        setCropBox(
          clampCropBox({
            x: state.startCropX + dx,
            y: state.startCropY + dy,
            size: state.startCropSize,
          }),
        );
      } else if (state.type === 'resize') {
        const dx = coords.x - state.startCropX;
        const dy = coords.y - state.startCropY;
        const newSize = Math.max(dx, dy);
        setCropBox(
          clampCropBox({
            x: state.startCropX,
            y: state.startCropY,
            size: newSize,
          }),
        );
      }
    };

    const handleMouseUp = () => {
      dragStateRef.current.type = 'none';
    };

    if (open && step === 'crop') {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [open, step, getImageCoords, clampCropBox]);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const coords = getImageCoords(e);
      const crop = cropBoxRef.current;
      dragStateRef.current = {
        type: 'drag',
        startMouseX: coords.x,
        startMouseY: coords.y,
        startCropX: crop.x,
        startCropY: crop.y,
        startCropSize: crop.size,
      };
    },
    [getImageCoords],
  );

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const coords = getImageCoords(e);
      const crop = cropBoxRef.current;
      dragStateRef.current = {
        type: 'resize',
        startMouseX: coords.x,
        startMouseY: coords.y,
        startCropX: crop.x,
        startCropY: crop.y,
        startCropSize: crop.size,
      };
    },
    [getImageCoords],
  );

  const handleReset = useCallback(() => {
    const d = displayDimsRef.current;
    const initialSize = Math.min(d.width, d.height) * 0.8;
    setCropBox(
      clampCropBox({
        x: (d.width - initialSize) / 2,
        y: (d.height - initialSize) / 2,
        size: initialSize,
      }),
    );
  }, [clampCropBox]);

  /** Produce the cropped blob and move to the confirm step */
  const handleCapture = useCallback(async () => {
    if (!imageDataUrl) return;
    setIsSaving(true);

    try {
      const img = new Image();
      img.src = imageDataUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const d = displayDimsRef.current;
      const crop = cropBoxRef.current;
      const scaleX = img.naturalWidth / d.width;
      const scaleY = img.naturalHeight / d.height;

      const sourceX = crop.x * scaleX;
      const sourceY = crop.y * scaleY;
      const sourceSizeX = crop.size * scaleX;
      const sourceSizeY = crop.size * scaleY;

      const outputSize = Math.min(
        Math.round(Math.max(sourceSizeX, sourceSizeY)),
        1024,
      );

      const canvas = document.createElement('canvas');
      canvas.width = outputSize;
      canvas.height = outputSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceSizeX,
        sourceSizeY,
        0,
        0,
        outputSize,
        outputSize,
      );

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png', 1.0);
      });

      if (!blob) return;

      const previewUrl = URL.createObjectURL(blob);
      setCroppedPreviewUrl(previewUrl);
      setCroppedBlob(blob);
      setStep('confirm');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    } finally {
      setIsSaving(false);
    }
  }, [imageDataUrl]);

  /** Set as thumbnail and close */
  const handleSetAsThumbnail = useCallback(() => {
    if (croppedBlob && onSetThumbnail) {
      onSetThumbnail(croppedBlob);
    }
    onOpenChange(false);
  }, [croppedBlob, onSetThumbnail, onOpenChange]);

  /** Download as file instead */
  const handleDownload = useCallback(() => {
    if (!croppedBlob) return;
    const url = URL.createObjectURL(croppedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'thumbnail.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onOpenChange(false);
  }, [croppedBlob, onOpenChange]);

  const handleBack = useCallback(() => {
    setStep('crop');
    if (croppedPreviewUrl) {
      URL.revokeObjectURL(croppedPreviewUrl);
      setCroppedPreviewUrl(null);
    }
    setCroppedBlob(null);
  }, [croppedPreviewUrl]);

  const imageOffset = getImageOffset();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl">
        {step === 'crop' ? (
          <>
            <DialogHeader>
              <DialogTitle>Capture Thumbnail</DialogTitle>
              <DialogDescription>
                Drag the square to position it, drag the corner handle to
                resize.
              </DialogDescription>
            </DialogHeader>

            <div
              ref={containerRef}
              className="relative mx-auto h-[60vh] w-full select-none overflow-hidden rounded-md bg-muted"
            >
              {imageDataUrl ? (
                <>
                  <img
                    ref={imageRef}
                    src={imageDataUrl}
                    alt="Preview"
                    className="absolute"
                    style={{
                      width: displayDimensions.width || undefined,
                      height: displayDimensions.height || undefined,
                      left: imageOffset.left,
                      top: imageOffset.top,
                    }}
                    onLoad={handleImageLoad}
                    draggable={false}
                  />

                  {displayDimensions.width > 0 && (
                    <>
                      <div
                        className="pointer-events-none absolute"
                        style={{
                          left: imageOffset.left,
                          top: imageOffset.top,
                          width: displayDimensions.width,
                          height: displayDimensions.height,
                        }}
                      >
                        <div
                          className="absolute left-0 top-0 bg-black/55"
                          style={{
                            width: '100%',
                            height: Math.max(0, cropBox.y),
                          }}
                        />
                        <div
                          className="absolute left-0 bg-black/55"
                          style={{
                            top: cropBox.y + cropBox.size,
                            width: '100%',
                            height: Math.max(
                              0,
                              displayDimensions.height -
                                cropBox.y -
                                cropBox.size,
                            ),
                          }}
                        />
                        <div
                          className="absolute left-0 bg-black/55"
                          style={{
                            top: cropBox.y,
                            width: Math.max(0, cropBox.x),
                            height: cropBox.size,
                          }}
                        />
                        <div
                          className="absolute bg-black/55"
                          style={{
                            left: cropBox.x + cropBox.size,
                            top: cropBox.y,
                            width: Math.max(
                              0,
                              displayDimensions.width -
                                cropBox.x -
                                cropBox.size,
                            ),
                            height: cropBox.size,
                          }}
                        />
                      </div>

                      <div
                        role="presentation"
                        className="absolute cursor-grab border-2 border-white active:cursor-grabbing"
                        style={{
                          left: imageOffset.left + cropBox.x,
                          top: imageOffset.top + cropBox.y,
                          width: cropBox.size,
                          height: cropBox.size,
                          boxShadow:
                            '0 0 0 1px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
                        }}
                        onMouseDown={startDrag}
                      >
                        <div className="pointer-events-none absolute inset-0">
                          <div className="absolute left-1/3 top-0 h-full w-px bg-white/25" />
                          <div className="absolute left-2/3 top-0 h-full w-px bg-white/25" />
                          <div className="absolute left-0 top-1/3 h-px w-full bg-white/25" />
                          <div className="absolute left-0 top-2/3 h-px w-full bg-white/25" />
                        </div>

                        <div className="pointer-events-none absolute -left-0.5 -top-0.5 h-3 w-3 border-l-[3px] border-t-[3px] border-white" />
                        <div className="pointer-events-none absolute -right-0.5 -top-0.5 h-3 w-3 border-r-[3px] border-t-[3px] border-white" />
                        <div className="pointer-events-none absolute -bottom-0.5 -left-0.5 h-3 w-3 border-b-[3px] border-l-[3px] border-white" />

                        <div
                          role="presentation"
                          className="absolute -bottom-1.5 -right-1.5 z-10 h-5 w-5 cursor-se-resize"
                          onMouseDown={startResize}
                        >
                          <div className="absolute bottom-0 right-0 h-3 w-3 border-b-[3px] border-r-[3px] border-white" />
                        </div>

                        <div className="absolute -top-7 left-0 rounded bg-black/80 px-2 py-0.5 text-xs font-medium text-white shadow-sm">
                          {displayDimensions.width > 0
                            ? `${Math.round((cropBox.size * (imageRef.current?.naturalWidth || 0)) / displayDimensions.width)}px`
                            : ''}
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No preview available. Make sure the 3D preview is visible.
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Reset
              </Button>
              <Button
                size="sm"
                onClick={handleCapture}
                disabled={!imageDataUrl || isSaving}
              >
                <Camera className="mr-1.5 h-4 w-4" />
                {isSaving ? 'Capturing...' : 'Capture'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          /* Confirm step */
          <>
            <DialogHeader>
              <DialogTitle>Set as Project Thumbnail?</DialogTitle>
              <DialogDescription>
                This image will be used as the thumbnail for your project,
                visible in the explore page and toolbar.
              </DialogDescription>
            </DialogHeader>

            <div className="flex justify-center py-4">
              {croppedPreviewUrl && (
                <img
                  src={croppedPreviewUrl}
                  alt="Cropped thumbnail"
                  className="max-h-[50vh] rounded-lg border border-border shadow-md"
                  style={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
                />
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" size="sm" onClick={handleBack}>
                Back
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="mr-1.5 h-4 w-4" />
                Download Only
              </Button>
              {canSetThumbnail && onSetThumbnail ? (
                <Button size="sm" onClick={handleSetAsThumbnail}>
                  <Camera className="mr-1.5 h-4 w-4" />
                  Set as Thumbnail
                </Button>
              ) : (
                <Button size="sm" onClick={handleDownload}>
                  <Download className="mr-1.5 h-4 w-4" />
                  Save
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
